import React, { useState, useEffect, useCallback, useRef } from "react";
import styled from "styled-components";
import { colors } from "utils";
import { request } from "hooks/useOkoApi/useOkoApi";
import { logError, LogCategory } from "utils/sentryLogger";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronRight, faSearch, faTimes } from "@fortawesome/free-solid-svg-icons";
import { groupClassesBySchoolYear, classDisplayName } from "components/shared/ClassroomFilter/groupClassesBySchoolYear";

// Stable per-instance id source (React 17 has no useId) for label/trigger association.
let selectDropdownSeq = 0;

// Simple select dropdown used for Class, Collab Level, Date Range, etc.
// `ariaLabel` names the trigger when there is no visible `label`.
// Builds the flat list of inline navigable rows. The non-current (past) classes
// live in the fly-out side panel (not inline), so the inline rows are the
// pinned/current-year option rows followed by the "SY 25-26 Classes" header at
// the bottom. Arrow-key nav / roving tabIndex operate over this list; the header
// opens the side panel. Each entry is { kind: "option", option } or { kind: "header", label }.
const buildNavigableRows = (options) => {
  const grouped = groupClassesBySchoolYear(options);
  const rows = [];
  for (const row of grouped) {
    if (row.type === "header") {
      rows.push({ kind: "header", label: row.label });
      break; // non-current rows are in the side panel, not inline
    }
    rows.push({ kind: "option", option: row.class });
  }
  return rows;
};

// Simple select dropdown used for Class, Collab Level, Date Range, etc.
// `ariaLabel` names the trigger when there is no visible `label`.
// School-year grouping is OPT-IN: only when `groupBySchoolYear` is true (the
// class pickers) are past-year options nested under a collapsible "SY 25-26
// Classes" fly-out. Every other dropdown (Question Type, Collaboration Level,
// Date Range, ...) renders a plain flat list.
// An option may carry `disabled` (optional): the row is then unselectable by click and
// keyboard, skipped by arrow-nav, and marked aria-disabled — matching MultiSelectDropdown.
// The 16px chevron of Figma's select (Manage Groups Filter row), verbatim.
const FigmaChevronDown = () => (
  <svg aria-hidden="true" focusable="false" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: "block", flexShrink: 0 }}>
    <path d="M4 6L8 10L12 6" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" stroke="#605DEC" />
  </svg>
);

// `appearance="figma"` opts into Figma's select (the Manage Groups Filter row);
// every other caller keeps the default appearance.
// `size="small"` (figma appearance only) is Figma's 160x36 select with Inter
// 14/20 type (the warm-up picker's Grades filter). `disabled` keeps it shut.
export const SelectDropdown = ({ label, ariaLabel, value, displayText, options, onChange, clearable, clearValue = null, groupBySchoolYear = false, appearance = "default", size, disabled = false }) => {
  const figma = appearance === "figma";
  const small = figma && size === "small";
  const figmaTags = small ? FIGMA_SMALL_TAGS : FIGMA_TAGS;
  const LabelTag = figma ? figmaTags.Label : Label;
  const TriggerTag = figma ? figmaTags.Trigger : SelectButton;
  const ValueTag = figma ? figmaTags.Value : SelectText;
  const MenuTag = figma ? figmaTags.Menu : DropdownMenu;
  const ItemTag = figma ? FigmaDropdownItem : DropdownItem;
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [expanded, setExpanded] = useState(true);
  const ref = useRef(null);
  const triggerRef = useRef(null);
  const itemRefs = useRef([]);
  const closeTimerRef = useRef(null);
  const labelIdRef = useRef(null);
  const groupIdRef = useRef(null);
  if (labelIdRef.current === null) labelIdRef.current = `select-dropdown-label-${++selectDropdownSeq}`;
  if (groupIdRef.current === null) groupIdRef.current = `select-dropdown-group-${selectDropdownSeq}`;
  const hasLabel = label != null && label !== "";
  const labelId = labelIdRef.current;
  const groupId = groupIdRef.current;

  // Flat (no grouping) unless this dropdown opted into school-year grouping.
  const grouped = groupBySchoolYear
    ? groupClassesBySchoolYear(options)
    : options.map((opt) => ({ type: "class", class: opt }));
  const headerRow = grouped.find((r) => r.type === "header");
  const hasGroup = Boolean(headerRow);
  const navRows = groupBySchoolYear
    ? buildNavigableRows(options)
    : options.map((opt) => ({ kind: "option", option: opt }));

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (isOpen && activeIndex >= 0) itemRefs.current[activeIndex]?.focus();
  }, [isOpen, activeIndex]);

  // Headers stay navigable (they open the fly-out); disabled option rows are skipped.
  // Mirrors MultiSelectDropdown.moveActive, but over this component's row shape —
  // navRows entries are { kind, option } wrappers, so the flag lives on row.option.
  const moveActive = (from, dir) => {
    let i = from;
    for (let step = 0; step < navRows.length; step += 1) {
      i += dir;
      if (i < 0 || i > navRows.length - 1) return from;
      const row = navRows[i];
      if (row.kind === "header" || !row.option?.disabled) return i;
    }
    return from;
  };

  const openMenu = () => {
    if (disabled) return;
    // The fly-out side panel always starts collapsed; it opens on hover only.
    const rows = buildNavigableRows(options);
    const selected = rows.findIndex((r) => r.kind === "option" && r.option.value === value);
    setExpanded(false);
    setActiveIndex(selected >= 0 ? selected : 0);
    setIsOpen(true);
  };
  const closeMenu = ({ returnFocus = true } = {}) => {
    setIsOpen(false);
    setActiveIndex(-1);
    if (returnFocus) triggerRef.current?.focus();
  };
  const selectOption = (optValue) => {
    onChange(optValue);
    closeMenu();
  };


  // Opens the side panel and moves focus into its first item. Used when the
  // header row is activated by keyboard (ArrowRight / Enter / Space).
  const openSubmenu = () => {
    setExpanded(true);
    // The submenu items mount on the same tick; focus the first after paint.
    requestAnimationFrame(() => {
      const panel = ref.current?.querySelector('[role="menu"]');
      panel?.querySelector('[role="menuitem"]')?.focus();
    });
  };

  const activateRow = (i) => {
    const row = navRows[i];
    if (!row) return;
    if (row.kind === "header") openSubmenu();
    else if (!row.option?.disabled) selectOption(row.option.value);
  };

  // Hover open/close for the fly-out panel. A short close delay lets the pointer
  // cross the header→panel edge without the panel snapping shut (submenu dead-zone).
  const openPanelOnHover = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setExpanded(true);
  };
  const closePanelOnLeave = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => setExpanded(false), 180);
  };

  const handleTriggerKeyDown = (e) => {
    if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      isOpen ? setActiveIndex((i) => moveActive(i, 1)) : openMenu();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      openMenu();
    }
  };
  const handleItemKeyDown = (e, i) => {
    const isHeader = navRows[i]?.kind === "header";
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIndex(moveActive(i, 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIndex(moveActive(i, -1)); }
    else if (e.key === "Home") { e.preventDefault(); setActiveIndex(moveActive(-1, 1)); }
    else if (e.key === "End") { e.preventDefault(); setActiveIndex(moveActive(navRows.length, -1)); }
    else if ((e.key === "ArrowRight") && isHeader) { e.preventDefault(); openSubmenu(); }
    else if (e.key === "Enter" || e.key === " ") { e.preventDefault(); activateRow(i); }
    else if (e.key === "Escape") { e.preventDefault(); closeMenu(); }
    else if (e.key === "Tab") { closeMenu({ returnFocus: false }); }
  };

  const showClear = clearable && value !== clearValue;
  const handleClear = (e) => {
    e.stopPropagation();
    onChange(clearValue);
  };

  // Renders an inline option row (current-year classes and the pinned sentinel).
  // The group header and its non-current rows render in the fly-out side panel.
  const renderRow = (row, i) => {
    const opt = row.option;
    return (
      <ItemTag
        key={opt.value ?? `opt-${i}`}
        ref={(el) => (itemRefs.current[i] = el)}
        type="button"
        role="option"
        aria-selected={opt.value === value}
        aria-disabled={opt.disabled || undefined}
        tabIndex={i === activeIndex ? 0 : -1}
        $active={opt.value === value}
        $disabled={opt.disabled}
        onClick={() => (opt.disabled ? null : selectOption(opt.value))}
        onKeyDown={(e) => handleItemKeyDown(e, i)}
      >
        {classDisplayName(opt)}
      </ItemTag>
    );
  };

  // The header row and the non-current option rows around it. In the fly-out
  // layout the non-current classes are NOT rendered inline; they live in a
  // side panel opened from the header, so the inline nav rows are the current
  // rows plus the header.
  const headerNavIndex = navRows.findIndex((r) => r.kind === "header");
  // The fly-out side panel holds ONLY the non-current (past) classes. Current-year
  // classes render inline (below the header), never in the panel.
  // Only real past-year classes (with a school_year record whose is_current is
  // false) live in the fly-out. A null-school_year class renders inline with the
  // current classes — matching the partition in groupClassesBySchoolYear — so it
  // must NOT also appear here, or it would render twice.
  const nonCurrentOptions = grouped.filter(
    (r) =>
      r.type === "class" &&
      r.class?.school_year != null &&
      r.class.school_year.is_current !== true
  );

  return (
    <DropdownContainer ref={ref}>
      {hasLabel && <LabelTag id={labelId}>{label}</LabelTag>}
      <TriggerTag
        ref={triggerRef}
        type="button"
        disabled={disabled || undefined}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-labelledby={hasLabel ? labelId : undefined}
        aria-label={!hasLabel ? ariaLabel : undefined}
        onClick={() => (isOpen ? closeMenu({ returnFocus: false }) : openMenu())}
        onKeyDown={handleTriggerKeyDown}
      >
        <ValueTag>{displayText}</ValueTag>
        {!showClear && (figma ? (
          <FigmaChevronDown />
        ) : (
          <FontAwesomeIcon icon={faChevronDown} style={{ fontSize: "16px", color: colors.nightlyWoods500 }} />
        ))}
      </TriggerTag>
      {showClear && (
        <ClearOverlayButton type="button" aria-label="Clear selected options" onClick={handleClear}>
          <FontAwesomeIcon icon={faTimes} style={{ fontSize: "12px", color: colors.milkGlass950 }} />
        </ClearOverlayButton>
      )}
      {isOpen && (
        <MenuTag role="listbox" aria-label={label || displayText} tabIndex={-1} $hasFlyout={hasGroup}>
          {navRows.slice(0, hasGroup ? headerNavIndex : navRows.length).map((row, i) => renderRow(row, i))}
          {hasGroup && (
            <SubmenuItem
              onMouseEnter={openPanelOnHover}
              onMouseLeave={closePanelOnLeave}
            >
              <GroupDisclosureHeader
                ref={(el) => (itemRefs.current[headerNavIndex] = el)}
                type="button"
                aria-haspopup="menu"
                aria-expanded={expanded}
                aria-controls={groupId}
                tabIndex={headerNavIndex === activeIndex ? 0 : -1}
                onKeyDown={(e) => handleItemKeyDown(e, headerNavIndex)}
              >
                <span>{headerRow.label}</span>
                <FontAwesomeIcon
                  icon={faChevronRight}
                  style={{ fontSize: "10px", color: colors.milkGlass950 }}
                />
              </GroupDisclosureHeader>
              {expanded && (
                <SubmenuPanel role="menu" id={groupId} aria-label={headerRow.label}>
                  {nonCurrentOptions.map((row) => {
                    const opt = row.class;
                    return (
                      <DropdownItem
                        key={opt.value ?? opt.id}
                        type="button"
                        role="menuitem"
                        aria-current={opt.value === value ? "true" : undefined}
                        $active={opt.value === value}
                        onClick={() => selectOption(opt.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Escape" || e.key === "ArrowLeft") {
                            e.preventDefault();
                            setExpanded(false);
                            itemRefs.current[headerNavIndex]?.focus();
                          } else if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            selectOption(opt.value);
                          }
                        }}
                      >
                        {classDisplayName(opt)}
                      </DropdownItem>
                    );
                  })}
                </SubmenuPanel>
              )}
            </SubmenuItem>
          )}
        </MenuTag>
      )}
    </DropdownContainer>
  );
};

// Searchable select for Student dropdown
export const SearchableSelectDropdown = ({ label, value, displayText, options, onChange, placeholder, clearable, clearValue = null }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);
  const ref = useRef(null);
  const inputRef = useRef(null);
  const itemRefs = useRef([]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setIsOpen(false);
        setSearchText("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const filtered = searchText
    ? options.filter((opt) =>
        opt.label.toLowerCase().includes(searchText.toLowerCase())
      )
    : options;

  useEffect(() => {
    if (isOpen && activeIndex >= 0) itemRefs.current[activeIndex]?.focus();
  }, [isOpen, activeIndex]);

  const closeMenu = () => {
    setIsOpen(false);
    setSearchText("");
    setActiveIndex(-1);
  };
  const selectOption = (optValue) => {
    onChange(optValue);
    closeMenu();
  };

  const handleInputKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (filtered.length) setActiveIndex(0);
    } else if (e.key === "Escape") {
      e.preventDefault();
      closeMenu();
    }
  };
  const handleItemKeyDown = (e, i) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIndex(Math.min(i + 1, filtered.length - 1)); }
    else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (i === 0) { setActiveIndex(-1); inputRef.current?.focus(); }
      else setActiveIndex(i - 1);
    }
    else if (e.key === "Enter" || e.key === " ") { e.preventDefault(); selectOption(filtered[i].value); }
    else if (e.key === "Escape") { e.preventDefault(); closeMenu(); inputRef.current?.focus(); }
  };

  const showClear = clearable && value !== clearValue;
  const handleClear = (e) => {
    e.stopPropagation();
    onChange(clearValue);
  };

  return (
    <DropdownContainer ref={ref}>
      <Label>{label}</Label>
      {isOpen ? (
        <SelectButton as="div" $focused>
          <SearchInput
            ref={inputRef}
            aria-label={label}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={handleInputKeyDown}
            placeholder={placeholder || "Type to search"}
          />
          <FontAwesomeIcon icon={faChevronDown} style={{ fontSize: "10px", color: colors.blue300 }} />
        </SelectButton>
      ) : (
        <SelectButton type="button" aria-label={`${label}: ${displayText}`} aria-haspopup="listbox" aria-expanded={isOpen} onClick={() => setIsOpen(true)}>
          <SelectText>{displayText}</SelectText>
          {!showClear && (
            <FontAwesomeIcon icon={faChevronDown} style={{ fontSize: "10px", color: colors.blue300 }} />
          )}
        </SelectButton>
      )}
      {!isOpen && showClear && (
        <ClearOverlayButton type="button" aria-label="Clear selected options" onClick={handleClear}>
          <FontAwesomeIcon icon={faTimes} style={{ fontSize: "12px", color: colors.milkGlass950 }} />
        </ClearOverlayButton>
      )}
      {isOpen && (
        <DropdownMenu role="listbox" aria-label={label || placeholder || displayText} tabIndex={0}>
          {filtered.length === 0 ? (
            <NoResults>
              <NoResultsTitle>No results found</NoResultsTitle>
              <NoResultsText>Try a different keyword or check your spelling.</NoResultsText>
            </NoResults>
          ) : (
            filtered.map((opt, i) => (
              <DropdownItem
                key={i}
                ref={(el) => (itemRefs.current[i] = el)}
                type="button"
                role="option"
                aria-selected={opt.value === value}
                tabIndex={i === activeIndex ? 0 : -1}
                $active={opt.value === value}
                onClick={() => selectOption(opt.value)}
                onKeyDown={(e) => handleItemKeyDown(e, i)}
              >
                {opt.label}
              </DropdownItem>
            ))
          )}
        </DropdownMenu>
      )}
    </DropdownContainer>
  );
};

// Type-ahead search for Domain/Standard
export const StandardSearchDropdown = ({
  label,
  value,
  displayLabel,
  onChange,
  classroomId,
  studentId,
  educatorId,
  dateRange,
  disabled = false,
  disabledPlaceholder,
  fetchOptions,
  placeholder = "All Domains/Standards",
  hideGroupHeaders = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const ref = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const itemRefs = useRef([]);

  // Flat, keyboard-navigable list of all selectable rows (domains + standards).
  // A row's `value` (when present) is what selection reports; `domain_code`
  // stays the displayed code, so callers can select by id (e.g. lesson_id).
  const flatItems = [];
  for (const domain of results) {
    flatItems.push({ code: domain.value ?? domain.domain_code, label: `${domain.domain_code}: ${domain.domain_name}` });
  }
  for (const domain of results) {
    for (const std of (domain.standards || [])) {
      flatItems.push({ code: std.standard_code, label: std.standard_name ? `${std.standard_code}: ${std.standard_name}` : std.standard_code });
    }
  }

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setIsOpen(false);
        setSearchText("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const buildSearchUrl = useCallback((query) => {
    const params = new URLSearchParams();
    if (query) params.append("q", query);
    if (classroomId) params.append("classroom_id", classroomId);
    if (studentId) params.append("student_id", studentId);
    if (educatorId) params.append("educator_id", educatorId);
    if (dateRange) params.append("date_range", dateRange);
    const qs = params.toString();
    return `account/session_history/standards_search${qs ? `?${qs}` : ""}`;
  }, [classroomId, studentId, educatorId, dateRange]);

  const searchStandards = useCallback(async (query) => {
    try {
      setIsSearching(true);
      const response = fetchOptions
        ? await fetchOptions(query)
        : await request(buildSearchUrl(query), { method: "GET" });
      setResults(response?.results || []);
    } catch (e) {
      logError("Failed to search standards", e, {
        category: LogCategory.ERROR,
      });
      setResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [buildSearchUrl, fetchOptions]);

  // Load full list when dropdown opens
  useEffect(() => {
    if (isOpen) {
      searchStandards(searchText);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setSearchText(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchStandards(val), 300);
  };

  const handleSelect = (code, label) => {
    onChange(code, label);
    setIsOpen(false);
    setSearchText("");
    setActiveIndex(-1);
  };

  useEffect(() => {
    if (isOpen && activeIndex >= 0) itemRefs.current[activeIndex]?.focus();
  }, [isOpen, activeIndex]);

  const handleInputKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (flatItems.length) setActiveIndex(0);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setIsOpen(false);
      setSearchText("");
    }
  };
  const handleItemKeyDown = (e, i) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIndex(Math.min(i + 1, flatItems.length - 1)); }
    else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (i === 0) { setActiveIndex(-1); inputRef.current?.focus(); }
      else setActiveIndex(i - 1);
    }
    else if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleSelect(flatItems[i].code, flatItems[i].label); }
    else if (e.key === "Escape") { e.preventDefault(); setIsOpen(false); setSearchText(""); setActiveIndex(-1); inputRef.current?.focus(); }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(null, null);
  };

  const hasValue = value !== null;

  return (
    <DropdownContainer ref={ref}>
      {label !== null && <Label>{label || "Domain/Standard"}</Label>}
      {isOpen && !disabled ? (
        <SelectButton as="div" $focused>
          <SearchInput
            ref={inputRef}
            aria-label={label || "Domain/Standard"}
            value={searchText}
            onChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
            placeholder="Type to search"
          />
          <ClearSearchButton aria-label="Clear selected options" onClick={() => {
            setIsOpen(false);
            setSearchText("");
          }}>
            <FontAwesomeIcon icon={faTimes} style={{ fontSize: "12px", color: colors.milkGlass950 }} />
          </ClearSearchButton>
        </SelectButton>
      ) : (
        <SelectButton
          onClick={() => { if (!disabled) setIsOpen(true); }}
          $disabled={disabled}
          disabled={disabled}
        >
          {hasValue && !disabled ? (
            <SelectText $truncate>{displayLabel}</SelectText>
          ) : (
            <>
              <SelectText $placeholder>
                {disabled ? (disabledPlaceholder || placeholder) : placeholder}
              </SelectText>
              <FontAwesomeIcon icon={faSearch} style={{ fontSize: "12px", color: colors.milkGlass950 }} />
            </>
          )}
        </SelectButton>
      )}
      {!(isOpen && !disabled) && hasValue && !disabled && (
        <ClearOverlayButton type="button" aria-label="Clear selected options" onClick={handleClear}>
          <FontAwesomeIcon icon={faTimes} style={{ fontSize: "12px", color: colors.milkGlass950 }} />
        </ClearOverlayButton>
      )}
      {isOpen && (
        <StandardsDropdownMenu role="listbox" aria-label={label || "Domain/Standard"} tabIndex={0}>
          {isSearching ? (
            <LoadingText>Searching...</LoadingText>
          ) : results.length === 0 ? (
            <NoResults>
              <NoResultsTitle>No results found</NoResultsTitle>
              <NoResultsText>{searchText ? "Try a different keyword or check your spelling." : "No standards available for the selected filters."}</NoResultsText>
            </NoResults>
          ) : (
            <>
              {!hideGroupHeaders && <GroupHeader>Domains</GroupHeader>}
              {results.map((domain, di) => {
                const domainValue = domain.value ?? domain.domain_code;
                return (
                  <StandardDropdownItem
                    key={domainValue}
                    ref={(el) => (itemRefs.current[di] = el)}
                    type="button"
                    role="option"
                    aria-selected={value === domainValue}
                    tabIndex={di === activeIndex ? 0 : -1}
                    onClick={() => handleSelect(domainValue, `${domain.domain_code}: ${domain.domain_name}`)}
                    onKeyDown={(e) => handleItemKeyDown(e, di)}
                    $active={value === domainValue}
                  >
                    {domain.domain_code}: {domain.domain_name}
                  </StandardDropdownItem>
                );
              })}
              {results.some((d) => d.standards.length > 0) && (
                <>
                  <GroupHeader $borderTop>Standards</GroupHeader>
                  {(() => {
                    let stdIdx = results.length;
                    return results.flatMap((domain) =>
                      domain.standards.map((std) => {
                        const idx = stdIdx++;
                        return (
                          <StandardDropdownItem
                            key={std.standard_code}
                            ref={(el) => (itemRefs.current[idx] = el)}
                            type="button"
                            role="option"
                            aria-selected={value === std.standard_code}
                            tabIndex={idx === activeIndex ? 0 : -1}
                            onClick={() => handleSelect(std.standard_code, std.standard_name ? `${std.standard_code}: ${std.standard_name}` : std.standard_code)}
                            onKeyDown={(e) => handleItemKeyDown(e, idx)}
                            $active={value === std.standard_code}
                          >
                            {std.standard_code}{std.standard_name ? `: ${std.standard_name}` : ""}
                          </StandardDropdownItem>
                        );
                      })
                    );
                  })()}
                </>
              )}
            </>
          )}
        </StandardsDropdownMenu>
      )}
    </DropdownContainer>
  );
};

// Date filter with available-dates API (daily + weekly + custom range)
const formatDateLabel = (isoDate) =>
  new Date(isoDate + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

const formatShortDate = (isoDate) =>
  new Date(isoDate + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" });

// Local calendar date: toISOString() is UTC and lands a day early east of UTC.
const toIso = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const getCalendarDays = (year, month) => {
  const first = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  // Monday = 0 start
  let startDow = (first.getDay() + 6) % 7;
  const days = [];
  // Previous month padding
  for (let i = startDow - 1; i >= 0; i--) {
    const d = new Date(year, month, -i);
    days.push({ date: toIso(d), day: d.getDate(), currentMonth: false });
  }
  // Current month
  for (let i = 1; i <= lastDay.getDate(); i++) {
    const d = new Date(year, month, i);
    days.push({ date: toIso(d), day: i, currentMonth: true });
  }
  // Next month padding to fill grid (6 rows max)
  while (days.length % 7 !== 0) {
    const d = new Date(year, month + 1, days.length - lastDay.getDate() - startDow + 1);
    days.push({ date: toIso(d), day: d.getDate(), currentMonth: false });
  }
  return days;
};

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];

export const DateFilterDropdown = ({
  label = "Date",
  classroomId,
  educatorId,
  startDate,
  endDate,
  selectedDateMode,
  onDateChange,
  onDatesLoaded,
  maxRangeDays = null,
  hideCustomRange = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [availableWeeks, setAvailableWeeks] = useState([]);
  const [availableDays, setAvailableDays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchFailed, setFetchFailed] = useState(false);
  const ref = useRef(null);
  const today = new Date();
  const [calMonth, setCalMonth] = useState(today.getMonth());
  const [calYear, setCalYear] = useState(today.getFullYear());
  const [rangeStart, setRangeStart] = useState(null);
  const [rangeEnd, setRangeEnd] = useState(null);
  const [focusedDate, setFocusedDate] = useState(null);
  const dayRefs = useRef({});
  const pendingFocusDate = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!classroomId) return;
    let cancelled = false;
    const fetchDates = async () => {
      setLoading(true);
      setFetchFailed(false);
      try {
        const base = new URLSearchParams();
        if (educatorId) base.set("educator_id", educatorId);
        const wp = new URLSearchParams(base); wp.set("mode", "weekly");
        const dp = new URLSearchParams(base); dp.set("mode", "daily");
        const [wd, dd] = await Promise.all([
          request(`class-report/${classroomId}/available-dates?${wp.toString()}`, { method: "GET" }),
          request(`class-report/${classroomId}/available-dates?${dp.toString()}`, { method: "GET" }),
        ]);
        if (!cancelled) {
          const weeks = wd?.weeks || [];
          const days = dd?.days || [];
          setAvailableWeeks(weeks);
          setAvailableDays(days);
          if (onDatesLoaded) onDatesLoaded(weeks, days);
        }
      } catch (e) {
        if (!cancelled) {
          logError("Failed to fetch available dates", e, { category: LogCategory.ERROR });
          setAvailableWeeks([]);
          setAvailableDays([]);
          setFetchFailed(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchDates();
    return () => { cancelled = true; };
  }, [classroomId, educatorId]);

  const handleCalendarPrev = () => {
    if (calMonth === 0) { setCalMonth(11); setCalYear(calYear - 1); }
    else setCalMonth(calMonth - 1);
  };
  const handleCalendarNext = () => {
    if (calMonth === 11) { setCalMonth(0); setCalYear(calYear + 1); }
    else setCalMonth(calMonth + 1);
  };
  const handleDayClick = (dateStr) => {
    if (!rangeStart || rangeEnd) {
      setRangeStart(dateStr);
      setRangeEnd(null);
    } else {
      if (maxRangeDays) {
        const diff = Math.abs(
          (new Date(dateStr + "T00:00:00") - new Date(rangeStart + "T00:00:00")) / 86400000
        );
        if (diff >= maxRangeDays) return;
      }
      let s = rangeStart, e = dateStr;
      if (e < s) { s = dateStr; e = rangeStart; }
      const nextDay = new Date(e + "T00:00:00");
      nextDay.setDate(nextDay.getDate() + 1);
      setRangeStart(null);
      setRangeEnd(null);
      onDateChange(s, toIso(nextDay), "custom");
      setIsOpen(false);
    }
  };

  const moveFocusToDate = (isoDate) => {
    setFocusedDate(isoDate);
    const d = new Date(isoDate + "T00:00:00");
    if (d.getMonth() !== calMonth || d.getFullYear() !== calYear) {
      // Day is in another month — switch the visible month, then focus once it renders.
      pendingFocusDate.current = isoDate;
      setCalMonth(d.getMonth());
      setCalYear(d.getFullYear());
    } else {
      dayRefs.current[isoDate]?.focus();
    }
  };

  const shiftDate = (isoDate, deltaDays) => {
    const d = new Date(isoDate + "T00:00:00");
    d.setDate(d.getDate() + deltaDays);
    return toIso(d);
  };

  const handleDayKeyDown = (e, isoDate) => {
    let nextDate = null;
    switch (e.key) {
      case "ArrowLeft": nextDate = shiftDate(isoDate, -1); break;
      case "ArrowRight": nextDate = shiftDate(isoDate, 1); break;
      case "ArrowUp": nextDate = shiftDate(isoDate, -7); break;
      case "ArrowDown": nextDate = shiftDate(isoDate, 7); break;
      case "Home": {
        const d = new Date(isoDate + "T00:00:00");
        nextDate = shiftDate(isoDate, -((d.getDay() + 6) % 7)); // back to Monday
        break;
      }
      case "End": {
        const d = new Date(isoDate + "T00:00:00");
        nextDate = shiftDate(isoDate, 6 - ((d.getDay() + 6) % 7)); // forward to Sunday
        break;
      }
      case "PageUp": nextDate = shiftDate(isoDate, -28); break;
      case "PageDown": nextDate = shiftDate(isoDate, 28); break;
      case "Enter":
      case " ":
        e.preventDefault();
        handleDayClick(isoDate);
        return;
      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        return;
      default:
        return;
    }
    e.preventDefault();
    moveFocusToDate(nextDate);
  };

  useEffect(() => {
    if (pendingFocusDate.current) {
      const target = pendingFocusDate.current;
      pendingFocusDate.current = null;
      dayRefs.current[target]?.focus();
    }
  }, [calMonth, calYear]);

  const calendarDays = getCalendarDays(calYear, calMonth);
  const todayStr = toIso(today);
  const rovingDate = focusedDate
    || rangeStart
    || (startDate && selectedDateMode === "custom" ? startDate : null)
    || todayStr;

  const displayText = (() => {
    if (loading) return "Loading...";
    if (startDate && endDate) {
      const endExcl = new Date(endDate + "T00:00:00");
      endExcl.setDate(endExcl.getDate() - 1);
      const endIncl = toIso(endExcl);
      if (startDate === endIncl) return formatDateLabel(startDate);
      const startYear = new Date(startDate + "T00:00:00").getFullYear();
      const endYear = endExcl.getFullYear();
      if (startYear !== endYear) {
        return `${formatDateLabel(startDate)} – ${formatDateLabel(endIncl)}`;
      }
      return `${formatShortDate(startDate)} – ${formatShortDate(endIncl)}, ${endYear}`;
    }
    if (startDate) return formatDateLabel(startDate);
    return "Select a date";
  })();

  return (
    <DropdownContainer ref={ref} style={{ maxWidth: "300px" }}>
      <Label>{label}</Label>
      <SelectButton type="button" aria-haspopup="dialog" aria-expanded={isOpen} onClick={() => setIsOpen(!isOpen)}>
        <SelectText>{displayText}</SelectText>
        <FontAwesomeIcon icon={faChevronDown} style={{ fontSize: "10px", color: colors.blue300 }} />
      </SelectButton>
      {isOpen && (
        <DropdownMenu style={{ maxHeight: "480px", width: "300px" }}>
          {availableDays.length > 0 && (
            <>
              <GroupHeader>Daily</GroupHeader>
              {availableDays.map((day) => (
                <DropdownItem
                  key={`day-${day.date}`}
                  type="button"
                  $active={startDate === day.date && selectedDateMode === "daily"}
                  onClick={() => {
                    const next = new Date(day.date + "T00:00:00");
                    next.setDate(next.getDate() + 1);
                    onDateChange(day.date, toIso(next), "daily");
                    setIsOpen(false);
                  }}
                >
                  {formatDateLabel(day.date)} ({day.session_count} {day.session_count === 1 ? "session" : "sessions"})
                </DropdownItem>
              ))}
            </>
          )}
          {availableWeeks.length > 0 && (
            <>
              <GroupHeader $borderTop={availableDays.length > 0}>Weekly</GroupHeader>
              {availableWeeks.map((week) => (
                <DropdownItem
                  key={`week-${week.week_of}`}
                  type="button"
                  $active={startDate === week.week_of && selectedDateMode === "weekly"}
                  onClick={() => {
                    const end = new Date(week.week_of + "T00:00:00");
                    end.setDate(end.getDate() + 7);
                    onDateChange(week.week_of, toIso(end), "weekly");
                    setIsOpen(false);
                  }}
                >
                  Week of {formatDateLabel(week.week_of)} ({week.session_count} {week.session_count === 1 ? "session" : "sessions"})
                </DropdownItem>
              ))}
            </>
          )}
          {hideCustomRange && !loading && availableDays.length === 0 && availableWeeks.length === 0 && (
            <GroupHeader>{fetchFailed ? "Couldn't load dates. Try again later." : "No recent sessions for this class."}</GroupHeader>
          )}
          {!hideCustomRange && (
          <>
          <GroupHeader $borderTop={availableDays.length > 0 || availableWeeks.length > 0}>Custom Range</GroupHeader>
          <CalendarContainer>
            <CalendarNav>
              <CalendarNavBtn type="button" aria-label="Previous month" onClick={handleCalendarPrev}>
                <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16" aria-hidden="true">
                  <path d="M11.78 5.22a.75.75 0 0 1 0 1.06L8.06 10l3.72 3.72a.75.75 0 1 1-1.06 1.06l-4.25-4.25a.75.75 0 0 1 0-1.06l4.25-4.25a.75.75 0 0 1 1.06 0Z" fillRule="evenodd" clipRule="evenodd" />
                </svg>
              </CalendarNavBtn>
              <CalendarMonthLabel>{MONTH_NAMES[calMonth]} {calYear}</CalendarMonthLabel>
              <CalendarNavBtn type="button" aria-label="Next month" onClick={handleCalendarNext}>
                <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
                  <path d="M8.22 5.22a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 0 1-1.06-1.06L11.94 10 8.22 6.28a.75.75 0 0 1 0-1.06Z" fillRule="evenodd" clipRule="evenodd" />
                </svg>
              </CalendarNavBtn>
            </CalendarNav>
            <CalendarGrid aria-label="Custom date range">
              {["M","T","W","T","F","S","S"].map((d, i) => (
                <CalendarDowCell key={i} aria-hidden="true">{d}</CalendarDowCell>
              ))}
              {(() => {
                // Exactly one day owns the tab stop. Prefer the roving date if it's
                // visible this month, else the first current-month day.
                const tabStopDate = calendarDays.some((d) => d.date === rovingDate)
                  ? rovingDate
                  : calendarDays.find((d) => d.currentMonth)?.date;
                return calendarDays.map((d) => {
                  const isToday = d.date === todayStr;
                  const isStart = d.date === (rangeStart || startDate);
                  const isEnd = d.date === rangeEnd;
                  const inRange = rangeStart && !rangeEnd ? false
                    : rangeStart && rangeEnd && d.date > rangeStart && d.date < rangeEnd;
                  const isSelected = isStart || isEnd;
                  const isDisabled = maxRangeDays && rangeStart && !rangeEnd
                    ? Math.abs(
                        (new Date(d.date + "T00:00:00") - new Date(rangeStart + "T00:00:00")) / 86400000
                      ) >= maxRangeDays
                    : false;
                  return (
                    <CalendarDayBtn
                      key={d.date}
                      ref={(el) => { dayRefs.current[d.date] = el; }}
                      type="button"
                      tabIndex={d.date === tabStopDate ? 0 : -1}
                      aria-label={formatDateLabel(d.date)}
                      aria-pressed={isSelected}
                      aria-disabled={isDisabled}
                      $currentMonth={d.currentMonth}
                      $today={isToday}
                      $selected={isSelected}
                      $inRange={inRange}
                      $outOfRange={isDisabled}
                      onClick={isDisabled ? undefined : () => handleDayClick(d.date)}
                      onKeyDown={isDisabled ? undefined : (e) => handleDayKeyDown(e, d.date)}
                    >
                      {d.day}
                    </CalendarDayBtn>
                  );
                });
              })()}
            </CalendarGrid>
            {rangeStart && !rangeEnd && (
              <CalendarHint>
                {maxRangeDays
                  ? `Click an end date (max ${maxRangeDays} days)`
                  : "Click an end date"}
              </CalendarHint>
            )}
          </CalendarContainer>
          </>
          )}
        </DropdownMenu>
      )}
    </DropdownContainer>
  );
};

// Styled components

export const FilterBar = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 0 16px;
  background: transparent;
  border-radius: 16px;
`;

export const ClearAllButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  height: 40px;
  background: transparent;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-family: "Inter", sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
  letter-spacing: 0.25px;
  color: ${colors.blue500};
  white-space: nowrap;
  flex: 0 0 auto;
  align-self: flex-end;

  &:hover {
    background: rgba(96, 93, 236, 0.08);
  }
`;

export const DropdownContainer = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
`;

const Label = styled.label`
  display: block;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: ${colors.nightlyWoods950};
  margin-bottom: 6px;
  white-space: nowrap;
`;

const SelectButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 8px 12px;
  background: ${(props) => (props.$disabled ? colors.neutral100 : "white")};
  border: 1px solid ${(props) => (props.$focused ? colors.blue500 : colors.blue300)};
  border-radius: 6px;
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
  min-height: 40px;
  gap: 8px;
  opacity: ${(props) => (props.$disabled ? 0.6 : 1)};

  &:hover {
    border-color: ${(props) => (props.$disabled ? colors.blue300 : colors.blue400)};
  }

  &:focus-visible {
    outline: 2px solid ${colors.blue500};
    outline-offset: 1px;
  }
`;

const SelectText = styled.span`
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: ${(props) => (props.$placeholder ? colors.milkGlass950 : colors.nightlyWoods500)};
  line-height: 24px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  &:focus:not(:focus-visible) {
    outline: none;
  }
  font-family: "Inter", sans-serif;
  font-size: 14px;
  color: ${colors.nightlyWoods500};
  background: transparent;
  min-width: 0;

  &::placeholder {
    color: ${colors.milkGlass950};
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  background: white;
  border: 1px solid #BAB9F5;
  border-radius: 8px;
  box-shadow: 0px 4px 12px rgba(14, 26, 49, 0.12);
  z-index: 100;
  /* When a fly-out side panel can open, don't clip it: the class menu is short
     so it never needs the scroll container. Otherwise keep the scrollable menu. */
  ${(props) =>
    props.$hasFlyout
      ? "overflow: visible;"
      : "max-height: 240px; overflow-y: auto;"}

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #B9B7B3;
    border-radius: 2px;
  }

  scrollbar-width: thin;
  scrollbar-color: #B9B7B3 transparent;
`;

const StandardsDropdownMenu = styled(DropdownMenu)`
  width: 420px;
  max-height: 360px;
`;

const DropdownItem = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  border: none;
  padding: 8px 16px;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  color: ${(props) => (props.$disabled ? colors.milkGlass950 : colors.nightlyWoods500)};
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
  line-height: 24px;
  background: ${(props) => (props.$active ? colors.blue50 : "transparent")};
  opacity: ${(props) => (props.$disabled ? 0.55 : 1)};

  &:hover {
    background: ${(props) => (props.$disabled ? "transparent" : colors.neutral100)};
  }

  &:focus-visible {
    outline: none;
    background: ${colors.neutral100};
    box-shadow: inset 2px 0 0 ${colors.blue500};
  }
`;

const StandardDropdownItem = styled(DropdownItem)`
  &:hover {
    background: #F4F4FD;
  }
`;

const GroupHeader = styled.div`
  padding: 8px 16px;
  font-family: "ABC Marfa", sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${colors.milkGlass950};
  line-height: 18px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  ${props => props.$borderTop && `border-top: 1px solid #ACAFAD;`}
`;

// Focusable disclosure control for the collapsible "SY 25-26 Classes" group.
// Never a role="option"; toggles the group on click / Enter / Space.
const GroupDisclosureHeader = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  text-align: left;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 8px 16px;
  font-family: "ABC Marfa", sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${colors.milkGlass950};
  line-height: 18px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  border-top: 1px solid #ACAFAD;

  &:hover {
    background: ${colors.neutral100};
  }
  &:focus-visible {
    outline: none;
    background: ${colors.neutral100};
    box-shadow: inset 2px 0 0 ${colors.blue500};
  }
`;

// Wrapper for the "SY 25-26 Classes" row + its side panel. Positioned relative
// so the fly-out panel anchors to the row's right edge (Google-Docs submenu).
const SubmenuItem = styled.div`
  position: relative;
`;

// The fly-out side panel that lists the prior-year classes. Opens to the right
// of the header row on hover / keyboard, mirroring the Google Docs submenu.
const SubmenuPanel = styled.div`
  position: absolute;
  top: 0;
  left: 100%;
  min-width: 200px;
  background: #ffffff;
  border: 1px solid ${colors.neutral200};
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  padding: 4px 0;
  z-index: 200;
`;

const ClearSearchButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

// Clear button rendered as a SIBLING overlay (not nested inside SelectButton)
// to avoid the axe "nested-interactive" violation (button inside button).
const ClearOverlayButton = styled(ClearSearchButton)`
  position: absolute;
  right: 12px;
  bottom: 0;
  height: 40px;

  &:focus-visible {
    outline: 2px solid ${colors.blue500};
    outline-offset: 1px;
    border-radius: 4px;
  }
`;

const NoResults = styled.div`
  padding: 8px 16px;
`;

const NoResultsTitle = styled.div`
  font-family: "ABC Marfa", sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${colors.milkGlass950};
  line-height: 18px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  margin-bottom: 4px;
`;

const NoResultsText = styled.div`
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #002E19;
  line-height: 20px;
  letter-spacing: 0;
`;

const LoadingText = styled.div`
  padding: 12px 16px;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  color: ${colors.milkGlass950};
`;

// Calendar styled components for DateFilterDropdown
const CalendarContainer = styled.div`
  padding: 8px 12px 12px;
`;

const CalendarNav = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const CalendarNavBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  color: ${colors.nightlyWoods500};
  &:hover { background: ${colors.neutral100}; }
  &:focus-visible {
    outline: 2px solid ${colors.blue500};
    outline-offset: 1px;
  }
`;

const CalendarMonthLabel = styled.span`
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: ${colors.nightlyWoods500};
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
  text-align: center;
`;

const CalendarDowCell = styled.div`
  font-family: "Inter", sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: ${colors.milkGlass950};
  padding: 4px 0;
`;

const CalendarDayBtn = styled.button`
  font-family: "Inter", sans-serif;
  font-size: 13px;
  border: none;
  cursor: ${(props) => (props.$outOfRange ? "not-allowed" : "pointer")};
  padding: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  opacity: ${(props) => (props.$outOfRange ? 0.25 : 1)};
  background: ${(props) =>
    props.$selected ? colors.blue500
    : props.$inRange ? colors.blue50
    : "transparent"};
  color: ${(props) =>
    props.$selected ? "white"
    : !props.$currentMonth ? colors.milkGlass900
    : colors.nightlyWoods500};
  font-weight: ${(props) => (props.$today ? "700" : "400")};
  outline: ${(props) => (props.$today && !props.$selected ? `1px solid ${colors.blue300}` : "none")};
  outline-offset: -1px;

  &:hover {
    background: ${(props) =>
      props.$outOfRange ? "transparent"
      : props.$selected ? colors.blue500
      : colors.blue50};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px white, 0 0 0 4px ${colors.blue500};
  }
`;

const CalendarHint = styled.div`
  font-family: "Inter", sans-serif;
  font-size: 12px;
  color: ${colors.milkGlass950};
  text-align: center;
  margin-top: 6px;
`;

// Figma's select for the Manage Groups Filter row (6141:4213, menu 6452:78402):
// 200x40, Roboto values, a 6px menu with its 1px stroke inside and no shadow.
const FIGMA_ROBOTO = '"Create Groups Menu Roboto", Roboto, sans-serif';
const FIGMA_INK = "#002E19";

const FigmaLabel = styled.label`
  color: #000000;
  display: block;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  letter-spacing: 0;
  line-height: normal;
  margin-bottom: 6px;
  white-space: nowrap;
`;

const FigmaSelectButton = styled.button`
  align-items: center;
  background: #ffffff;
  border: 1px solid ${colors.blue300};
  border-radius: 6px;
  box-sizing: border-box;
  cursor: pointer;
  display: flex;
  gap: 8px;
  height: 40px;
  justify-content: space-between;
  padding: 7px 11px;
  width: 200px;
`;

const FigmaSelectText = styled.span`
  color: ${FIGMA_INK};
  font-family: ${FIGMA_ROBOTO};
  font-size: 14px;
  font-weight: 400;
  letter-spacing: 0;
  line-height: 24px;
  overflow: hidden;
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const FigmaSmallLabel = styled(FigmaLabel)`
  color: ${FIGMA_INK};
  line-height: 20px;
`;

// Keeps the 200px trigger's 7px 11px: with the 1px border the value and the
// chevron sit 12px in, where Figma's inside stroke puts them.
const FigmaSmallSelectButton = styled(FigmaSelectButton)`
  height: 36px;
  width: 160px;

  &:disabled {
    cursor: default;
  }
`;

const FigmaSmallSelectText = styled(FigmaSelectText)`
  font-family: Inter, sans-serif;
  line-height: 20px;
`;

const FigmaDropdownMenu = styled.div`
  background: #ffffff;
  border: none;
  border-radius: 6px;
  box-shadow: none;
  box-sizing: border-box;
  left: 0;
  margin-top: 4px;
  overflow: ${(props) => (props.$hasFlyout ? "visible" : "hidden")};
  position: absolute;
  top: 100%;
  width: 200px;
  z-index: 200;

  &::after {
    border: 1px solid ${colors.blue300};
    border-radius: 6px;
    bottom: 0;
    content: "";
    left: 0;
    pointer-events: none;
    position: absolute;
    right: 0;
    top: 0;
  }
`;

const FigmaSmallDropdownMenu = styled(FigmaDropdownMenu)`
  width: 160px;
`;

const FIGMA_TAGS = { Label: FigmaLabel, Trigger: FigmaSelectButton, Value: FigmaSelectText, Menu: FigmaDropdownMenu };
const FIGMA_SMALL_TAGS = {
  Label: FigmaSmallLabel,
  Trigger: FigmaSmallSelectButton,
  Value: FigmaSmallSelectText,
  Menu: FigmaSmallDropdownMenu,
};

const FigmaDropdownItem = styled.button`
  background: #ffffff;
  border: none;
  box-sizing: border-box;
  color: ${FIGMA_INK};
  cursor: pointer;
  display: block;
  font-family: ${FIGMA_ROBOTO};
  font-size: 14px;
  font-weight: 400;
  height: 40px;
  line-height: 24px;
  padding: 8px 16px;
  text-align: left;
  white-space: nowrap;
  width: 100%;

  &:hover {
    background: ${colors.blue50};
  }

  &:focus {
    background: ${colors.blue50};
    outline: none;
  }

  &:focus-visible {
    box-shadow: inset 3px 0 0 ${colors.blue500};
    outline: none;
  }
`;
